import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Battery, Zap, Leaf, Building } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const PrologisMicrogridArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-green-500 text-white">
                <Battery className="h-3 w-3 mr-1" />
                {t('article.prologis.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.prologis.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.prologis.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.prologis.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://www.prologis.com/sites/corporate/files/images/2025/11/Prologis%20Park%20Almere%20DC5-blog%20banner%20image.jpg"
              alt="Prologis microgrid-powered logistics facility with solar panels" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-yellow-600" />
                  {t('article.prologis.stat1')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-yellow-600">{t('article.prologis.statValue1')}</div>
                <p className="text-sm text-gray-600">{t('article.prologis.statDesc1')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Battery className="h-5 w-5 mr-2 text-green-600" />
                  {t('article.prologis.stat2')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('article.prologis.statValue2')}</div>
                <p className="text-sm text-gray-600">{t('article.prologis.statDesc2')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Leaf className="h-5 w-5 mr-2 text-blue-600" />
                  {t('article.prologis.stat3')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('article.prologis.statValue3')}</div>
                <p className="text-sm text-gray-600">{t('article.prologis.statDesc3')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('article.prologis.heading1')}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.prologis.content1')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.heading2')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.prologis.content2')}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>{t('article.prologis.techSolar').split(':')[0]}:</strong> {t('article.prologis.techSolar').split(':')[1]}</li>
              <li><strong>{t('article.prologis.techBattery').split(':')[0]}:</strong> {t('article.prologis.techBattery').split(':')[1]}</li>
              <li><strong>{t('article.prologis.techSmart').split(':')[0]}:</strong> {t('article.prologis.techSmart').split(':')[1]}</li>
              <li><strong>{t('article.prologis.techGrid').split(':')[0]}:</strong> {t('article.prologis.techGrid').split(':')[1]}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.sustainabilityTitle')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.prologis.impactTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.prologis.impactCarbon').split(':')[0]}:</strong> {t('article.prologis.impactCarbon').split(':')[1]}</li>
                <li><strong>{t('article.prologis.impactRenewable').split(':')[0]}:</strong> {t('article.prologis.impactRenewable').split(':')[1]}</li>
                <li><strong>{t('article.prologis.impactEfficiency').split(':')[0]}:</strong> {t('article.prologis.impactEfficiency').split(':')[1]}</li>
                <li><strong>{t('article.prologis.impactResilience').split(':')[0]}:</strong> {t('article.prologis.impactResilience').split(':')[1]}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.operationalTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.prologis.operationalContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.smartTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.prologis.smartContent')}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>{t('article.prologis.smartOptimize').split(':')[0]}:</strong> {t('article.prologis.smartOptimize').split(':')[1]}</li>
              <li><strong>{t('article.prologis.smartPredict').split(':')[0]}:</strong> {t('article.prologis.smartPredict').split(':')[1]}</li>
              <li><strong>{t('article.prologis.smartManage').split(':')[0]}:</strong> {t('article.prologis.smartManage').split(':')[1]}</li>
              <li><strong>{t('article.prologis.smartGrid').split(':')[0]}:</strong> {t('article.prologis.smartGrid').split(':')[1]}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Leadership</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.prologis.commitmentTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.prologis.firstMover').split(':')[0]}:</strong> {t('article.prologis.firstMover').split(':')[1]}</li>
                <li><strong>{t('article.prologis.innovationHub').split(':')[0]}:</strong> {t('article.prologis.innovationHub').split(':')[1]}</li>
                <li><strong>{t('article.prologis.industryStandard').split(':')[0]}:</strong> {t('article.prologis.industryStandard').split(':')[1]}</li>
                <li><strong>{t('article.prologis.scalableModel').split(':')[0]}:</strong> {t('article.prologis.scalableModel').split(':')[1]}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.economicTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.prologis.economicContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.scalabilityTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.prologis.scalabilityContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.futureTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.prologis.futureContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.prologis.customerTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.prologis.customerContent')}
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">{t('article.prologis.sourceTitle')}</h4>
              <p className="text-sm text-gray-600">
                {t('article.prologis.sourceText')}
                <a href="https://www.prologis.com/insights-news/blog/prologis-unveils-its-first-microgrid-powered-logistics-facility-europe" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  {t('article.prologis.viewSource')}
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default PrologisMicrogridArticle;